import React from 'react';
import { Separator } from '@cuemath/comps';
import { Button } from '@cuemath/web-comps';

import ChipGroup from '../chips';
import RangePicker from '../range-picker';
import CountrySelect from '../country-select';
import { Card, ChipGroupsRow, FilterField, FieldLabelText, ActionsField } from '../../styled-comps';
import {
  YEAR_OPTIONS,
  MONTH_OPTIONS,
  REGION_OPTIONS,
  USER_TYPE_OPTIONS,
  PERIOD_MODES,
  PERIOD_MODE_OPTIONS,
  ANALYSIS_MIN_DATE,
} from '../../constants';

const today = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// The "pick a period" chip card shared by the Category, Non-Content and Blogs
// tabs. A Period toggle switches between two mutually-exclusive selectors — only
// one is ever shown:
//   * By month   -> Year + Month chips (month-wise breakdown, the default)
//   * Custom dates -> a date-range picker (any span from Apr 2025)
// Region + User type multi-selects apply to both, and (when a region is chosen)
// a per-region country multiselect. Nothing here fires a query directly — edits
// touch the draft and the query runs only on Apply. Purely presentational — the
// owning tab (via usePeriodSelection) keeps the draft/applied state.
const PeriodChips = ({
  mode,
  onModeChange,
  year,
  onYearChange,
  monthIndex,
  onMonthChange,
  rangeStart,
  rangeEnd,
  onRangeChange,
  regions,
  onRegionsChange,
  countries,
  onCountriesChange,
  userTypes,
  onUserTypesChange,
  hasUnapplied,
  onApply,
  onReset,
}) => (
  <Card>
    <ChipGroup
      label="Period"
      options={PERIOD_MODE_OPTIONS}
      value={mode}
      onChange={onModeChange}
    />
    <Separator height={10} transparent />
    {mode === PERIOD_MODES.RANGE ? (
      <FilterField $width="250px">
        <FieldLabelText variant="eyebrow2">Custom date range</FieldLabelText>
        <RangePicker
          start={rangeStart}
          end={rangeEnd}
          minDate={ANALYSIS_MIN_DATE}
          maxDate={today()}
          onChange={onRangeChange}
        />
      </FilterField>
    ) : (
      <>
        <ChipGroup label="Year" options={YEAR_OPTIONS} value={year} onChange={onYearChange} />
        <Separator height={10} transparent />
        <ChipGroup
          label="Month"
          options={MONTH_OPTIONS}
          value={monthIndex}
          onChange={onMonthChange}
        />
      </>
    )}
    <Separator height={10} transparent />
    <ChipGroupsRow>
      <ChipGroup
        label="Region"
        options={REGION_OPTIONS}
        value={regions}
        onChange={onRegionsChange}
        multi
      />
      <ChipGroup
        label="User type"
        options={USER_TYPE_OPTIONS}
        value={userTypes}
        onChange={onUserTypesChange}
        multi
      />
    </ChipGroupsRow>
    {!!(regions && regions.length) && (
      <>
        <Separator height={10} transparent />
        <CountrySelect regions={regions} value={countries} onChange={onCountriesChange} />
      </>
    )}
    <Separator height={14} transparent />
    <ActionsField>
      <Button size="small" label={hasUnapplied ? 'Apply *' : 'Apply'} onClick={onApply} />
      <Button size="small" variant="tertiaryWithUnderline" label="Reset" onClick={onReset} />
    </ActionsField>
  </Card>
);

export default PeriodChips;
